import { useCallback, useEffect, useState } from "react";
import { ConfirmModal } from "./ConfirmModal";
import { Pagination, PAGE_SIZE } from "./Pagination";

/**
 * Plano e uso da organização. O catálogo LOCAL de planos é a ÚNICA fonte dos
 * limites (assentos, workspaces, orçamento de IA) - não há mais "planos padrão
 * da plataforma", vínculo externo nem overrides locais. Tudo vem do plano.
 */
interface BillingStatus {
  planId: string | null;
  localPlanId: string | null;
  planName: string | null;
  status: string | null;
  limits: { seats: number | null; repos: number | null; budgetUsdMonthly: number | null };
  usage: { seats: number; repos: number };
  spentUsdThisMonth: number;
  /** Gasto do mês por motor de IA (claude/codex). */
  spentByAgentThisMonth?: Record<"claude" | "codex" | "gemini", { costUsd: number; tokens: number; responses: number }>;
  /** Período de teste local: plano contratado x vigente x prazo + ativação. */
  trial?: {
    mode: "linked" | "active" | "trialing" | "expired" | "blocked" | "none";
    reason?: "trial" | "payment";
    endsAt: string | null;
    daysLeft: number | null;
    chosenPlanId: string | null;
    chosenPlanName: string | null;
    effectivePlanId: string;
    effectivePlanName: string;
    activatedAt: string | null;
    lastManualPaymentAt: string | null;
  };
  alertWebhookUrl: string | null;
  alertThresholds: { softPercent: number; hardPercent: number };
  /** Downgrade agendado pelo admin (vale na próxima mensalidade). */
  pendingPlanId?: string | null;
  pendingPlanName?: string | null;
  /** Pacotes adicionais aplicados (extras liberados já embutidos em limits). */
  addons?: {
    items: AddonRow[];
    extras: { repos: number; budgetUsd: number; apiCalls: number; seats: number; workspaces: number };
  };
}

/** Tipos de pacote adicional - espelham os limites do plano. */
type AddonKind = "repos" | "ai_budget" | "api_calls" | "seats" | "workspaces";

/** Pacote adicional da org. */
interface AddonRow {
  id: string;
  kind: AddonKind;
  qty: number;
  label: string;
  /** pending = contratado com cobrança, aguardando o pagamento pra liberar. */
  status?: "pending" | "active" | "removed";
  invoiceId?: string | null;
  invoiceStatus?: "open" | "paid" | "canceled" | null;
  invoiceAmountBrl?: number | null;
  packageId?: string | null;
  note?: string | null;
  appliedAt: string;
  appliedBy: string;
  removedAt?: string | null;
  removedBy?: string | null;
}

/** Pacote do catálogo global disponível pra contratação. */
interface CatalogPackageRow {
  id: string;
  kind: AddonRow["kind"];
  qty: number;
  priceBrl: number;
  label: string;
  active: boolean;
}

const STATUS_PT: Record<string, string> = {
  trialing: "em teste",
  active: "ativa",
  past_due: "pagamento pendente",
  canceled: "cancelada",
};

/** Barra "usado de limite". */
function UsageRow({ label, used, limit, suffix }: { label: string; used: number; limit: number | null; suffix?: string }) {
  const unlimited = limit === null || limit === Number.POSITIVE_INFINITY;
  const pct = unlimited || !limit ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const near = !unlimited && pct >= 90;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 3 }}>
        <span style={{ fontWeight: 600 }}>{label}</span>
        <span className="admin-user-meta">
          {used}
          {suffix ?? ""} {unlimited ? "· ilimitado" : `de ${limit}${suffix ?? ""}`}
        </span>
      </div>
      <div style={{ height: 6, borderRadius: 999, background: "var(--bg-elevated, #e9e9e9)", overflow: "hidden" }}>
        <div
          style={{
            width: unlimited ? "100%" : `${pct}%`,
            height: "100%",
            borderRadius: 999,
            background: unlimited ? "var(--border, #cfcfcf)" : near ? "var(--danger, #c0392b)" : "var(--accent, #c15f3c)",
            opacity: unlimited ? 0.35 : 1,
            transition: "width .3s",
          }}
        />
      </div>
    </div>
  );
}

interface Props {
  /** Sysadmin só - mostra a org escolhida no seletor em vez da própria. */
  orgId?: string;
  /** Operador da plataforma - habilita as operações de billing (trial/pagamento). */
  isSysadmin?: boolean;
}

export function BillingPanel({ orgId, isSysadmin = false }: Props) {
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  // faturas no pai: alimentam o card de destaque (ao lado do plano) E a lista
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [alertWebhookUrl, setAlertWebhookUrl] = useState("");
  const [softPercent, setSoftPercent] = useState(80);
  const [hardPercent, setHardPercent] = useState(100);
  const [alertBusy, setAlertBusy] = useState(false);
  const [alertError, setAlertError] = useState<string | null>(null);
  const [alertSaved, setAlertSaved] = useState(false);
  const [opsBusy, setOpsBusy] = useState(false);
  const [opsError, setOpsError] = useState<string | null>(null);
  const [extendDays, setExtendDays] = useState(7);

  const qs = orgId ? `?orgId=${encodeURIComponent(orgId)}` : "";

  const billingOp = async (action: "start_trial" | "extend_trial" | "mark_paid" | "deactivate", days?: number) => {
    setOpsBusy(true);
    setOpsError(null);
    try {
      const r = await fetch(`/api/admin/org/billing-local${qs}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ action, days }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setOpsError(j.error ?? "falha na operação");
        return;
      }
      if (j.billing) setBilling(j.billing as BillingStatus);
    } finally {
      setOpsBusy(false);
    }
  };

  const load = useCallback(async () => {
    const [r, ri] = await Promise.all([fetch(`/api/admin/billing${qs}`), fetch(`/api/admin/invoices${qs}`)]);
    if (r.ok) {
      const j = (await r.json()) as BillingStatus;
      setBilling(j);
      setAlertWebhookUrl(j.alertWebhookUrl ?? "");
      setSoftPercent(j.alertThresholds.softPercent);
      setHardPercent(j.alertThresholds.hardPercent);
    }
    if (ri.ok) {
      const ji = (await ri.json()) as { items?: InvoiceRow[] };
      setInvoices(ji.items ?? []);
    }
  }, [qs]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveAlerts = async () => {
    setAlertBusy(true);
    setAlertError(null);
    setAlertSaved(false);
    try {
      const r = await fetch(`/api/admin/billing/alerts${qs}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ webhookUrl: alertWebhookUrl.trim(), softPercent, hardPercent }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) setAlertError(j.error ?? "Falha ao salvar.");
      else {
        setBilling(j);
        setAlertSaved(true);
      }
    } finally {
      setAlertBusy(false);
    }
  };

  if (!billing) return null;

  // faturas em aberto, da mais urgente (menor vencimento) pra frente - a
  // primeira vira o card de destaque ao lado do plano
  const openInvoices = invoices
    .filter((i) => i.status === "open")
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  const nextOpenInvoice = openInvoices[0] ?? null;
  const localBilling = Boolean(billing.trial && billing.trial.mode !== "linked");

  return (
    <>
      <div className="plan-row">
      <div className="admin-block plan-block">
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Plano atual</h3>
          <strong style={{ fontSize: 18 }}>{billing.planName ?? "—"}</strong>
          {billing.status && <span className="badge ok">{STATUS_PT[billing.status] ?? billing.status}</span>}
          {billing.trial?.mode === "trialing" && <span className="badge warn">em teste</span>}
          {billing.trial?.mode === "expired" && (
            <span className="badge err">{billing.trial.reason === "payment" ? "pagamento pendente" : "teste encerrado"}</span>
          )}
          {billing.trial?.mode === "blocked" && (
            <span className="badge err">{billing.trial.reason === "trial" ? "bloqueada (teste encerrado)" : "bloqueada por pagamento"}</span>
          )}
          {billing.trial?.mode === "active" && <span className="badge ok">assinatura ativa</span>}
        </div>
        {/* Durante o teste, as 3 informações: plano contratado, plano vigente e prazo */}
        {billing.trial && (billing.trial.mode === "trialing" || billing.trial.mode === "expired") && (
          <div className="trial-info" style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
            <div className="trial-info-card">
              <span className="admin-user-meta">Plano contratado</span>
              <strong>{billing.trial.chosenPlanName ?? "—"}</strong>
              <span className="admin-user-meta">vale após a ativação</span>
            </div>
            <div className="trial-info-card">
              <span className="admin-user-meta">Plano vigente no teste</span>
              <strong>{billing.trial.effectivePlanName}</strong>
              <span className="admin-user-meta">limites aplicados abaixo</span>
            </div>
            <div className="trial-info-card">
              <span className="admin-user-meta">Período de teste</span>
              <strong>
                {billing.trial.mode === "expired"
                  ? "encerrado"
                  : billing.trial.daysLeft === 1
                    ? "falta 1 dia"
                    : `faltam ${billing.trial.daysLeft} dias`}
              </strong>
              <span className="admin-user-meta">
                {billing.trial.endsAt
                  ? `até ${new Date(billing.trial.endsAt).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })}`
                  : "—"}
              </span>
            </div>
          </div>
        )}
        <UsageRow label="Assentos" used={billing.usage.seats} limit={billing.limits.seats} />
        <UsageRow label="Repositórios" used={billing.usage.repos} limit={billing.limits.repos} />
        <UsageRow
          label="Orçamento de IA no mês (US$)"
          used={Number(billing.spentUsdThisMonth.toFixed(2))}
          limit={billing.limits.budgetUsdMonthly}
        />
        {billing.spentByAgentThisMonth &&
          (["claude", "codex", "gemini"] as const).some((ag) => (billing.spentByAgentThisMonth?.[ag]?.responses ?? 0) > 0) && (
            <p className="admin-user-meta" style={{ marginTop: 4 }}>
              Por motor:{" "}
              {(["claude", "codex", "gemini"] as const)
                .filter((ag) => (billing.spentByAgentThisMonth?.[ag]?.responses ?? 0) > 0)
                .map(
                  (ag) =>
                    `${ag === "claude" ? "Claude" : ag === "codex" ? "Codex" : "Gemini"} $${billing.spentByAgentThisMonth![ag].costUsd.toFixed(2)} (${billing.spentByAgentThisMonth![ag].responses} resp.)`,
                )
                .join(" · ")}
            </p>
          )}
        {billing.addons &&
          (() => {
            const ex = billing.addons.extras;
            const parts = [
              ex.repos > 0 && `+${ex.repos} repositório(s)`,
              ex.budgetUsd > 0 && `+US$ ${ex.budgetUsd.toFixed(2)}/mês de IA`,
              ex.apiCalls > 0 && `+${ex.apiCalls.toLocaleString("pt-BR")} req/mês de API`,
              ex.seats > 0 && `+${ex.seats} usuário(s)`,
              ex.workspaces > 0 && `+${ex.workspaces} workspace(s)`,
            ].filter(Boolean) as string[];
            return parts.length > 0 ? (
              <p className="admin-user-meta" style={{ marginTop: 4 }}>
                Os limites acima já incluem pacotes adicionais: {parts.join(" · ")}.
              </p>
            ) : null;
          })()}
        <p className="admin-user-meta" style={{ marginTop: 8 }}>
          Os limites vêm do plano da organização. Para mudar de plano, use a seção de planos.
        </p>
      </div>
      {localBilling && nextOpenInvoice && (
        <OpenInvoiceCard invoice={nextOpenInvoice} count={openInvoices.length} qs={qs} />
      )}
      </div>

      <div className="admin-block">
        <h4 style={{ marginBottom: 6 }}>Alertas de orçamento</h4>
        <p className="admin-user-meta" style={{ marginBottom: 8 }}>
          Webhook (ex.: Teams) avisado quando o gasto de IA do mês atingir os limiares abaixo. Sem webhook, o
          bloqueio de orçamento continua valendo, só os avisos ficam desligados.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 8 }}>
          <label className="field" style={{ flex: "1 1 260px" }}>
            <span>URL do webhook (opcional)</span>
            <input
              type="url"
              value={alertWebhookUrl}
              onChange={(e) => setAlertWebhookUrl(e.target.value)}
              placeholder="https://..."
              spellCheck={false}
            />
          </label>
          <label className="field" style={{ width: 130 }}>
            <span>Aviso (%)</span>
            <input type="number" min={1} max={99} value={softPercent} onChange={(e) => setSoftPercent(Number(e.target.value))} />
          </label>
          <label className="field" style={{ width: 130 }}>
            <span>Bloqueio (%)</span>
            <input type="number" min={1} max={100} value={hardPercent} onChange={(e) => setHardPercent(Number(e.target.value))} />
          </label>
          <button className="btn-approve" disabled={alertBusy} onClick={() => void saveAlerts()} type="button">
            Salvar
          </button>
        </div>
        {alertError && <div className="auth-error" style={{ marginBottom: 8 }}>{alertError}</div>}
        {alertSaved && !alertError && (
          <div className="admin-empty small" style={{ color: "var(--success, #2a9d5c)", marginBottom: 8 }}>
            Configuração salva.
          </div>
        )}
      </div>

      {/* Operações de billing local - só o operador da plataforma (espelho do Suite) */}
      {isSysadmin && billing.trial && billing.trial.mode !== "linked" && (
        <div className="admin-block">
          <h4 style={{ marginBottom: 6 }}>Operações (operador da plataforma)</h4>
          <p className="admin-user-meta" style={{ marginBottom: 10 }}>
            Estenda o período de teste, registre um pagamento feito por fora (ativa a assinatura e libera o
            plano contratado) ou encerre a ativação.
            {billing.trial.lastManualPaymentAt && (
              <>
                {" "}Último pagamento manual:{" "}
                {new Date(billing.trial.lastManualPaymentAt).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })}.
              </>
            )}
          </p>
          {opsError && <div className="auth-error" style={{ marginBottom: 8 }}>{opsError}</div>}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label className="field" style={{ width: 120 }}>
              <span>Dias</span>
              <input type="number" min={1} max={365} value={extendDays} onChange={(e) => setExtendDays(Number(e.target.value))} />
            </label>
            {billing.trial.mode === "trialing" || billing.trial.mode === "expired" ? (
              <button className="btn-approve" type="button" disabled={opsBusy} onClick={() => void billingOp("extend_trial", extendDays)}>
                Estender teste
              </button>
            ) : (
              <button className="btn-approve" type="button" disabled={opsBusy} onClick={() => void billingOp("start_trial", extendDays)}>
                Colocar em período de teste
              </button>
            )}
            {billing.trial.mode !== "active" ? (
              <button className="btn-approve" type="button" disabled={opsBusy} onClick={() => void billingOp("mark_paid")}>
                Registrar pagamento (ativar)
              </button>
            ) : (
              <>
                <button className="btn-approve" type="button" disabled={opsBusy} onClick={() => void billingOp("mark_paid")}>
                  Registrar novo pagamento
                </button>
                <button className="btn-reject" type="button" disabled={opsBusy} onClick={() => void billingOp("deactivate")}>
                  Encerrar ativação
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Relatório de consumo de IA (todo admin) */}
      <UsageReportBlock qs={qs} />

      {/* Troca de plano self-service (admin da org, billing local) */}
      {localBilling && <PlanChangeBlock billing={billing} qs={qs} onChanged={(b) => (b ? setBilling(b) : void load())} />}

      {/* Pacotes adicionais: admin vê; sysadmin aplica/remove (cobrança opcional) */}
      {billing.trial && billing.trial.mode !== "linked" && (
        <AddonsBlock billing={billing} qs={qs} isSysadmin={isSysadmin} onChanged={(b) => (b ? setBilling(b) : void load())} />
      )}

      {/* Faturamento local: faturas da organização (admin vê; sysadmin gera/paga/cancela) */}
      {localBilling && <InvoicesBlock items={invoices} qs={qs} isSysadmin={isSysadmin} onChanged={load} />}
    </>
  );
}

/**
 * Competência (YYYY-MM) como SELECT pt-BR ("outubro/2026") - input type=month
 * não tem suporte no Firefox (renderiza texto cru "2026-10", fora do padrão).
 */
function MonthField({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  const options: { v: string; label: string }[] = [];
  const base = new Date();
  for (let i = 3; i >= -23; i--) {
    const d = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + i, 1));
    const v = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
    options.push({ v, label: d.toLocaleDateString("pt-BR", { month: "long", year: "numeric", timeZone: "UTC" }) });
  }
  if (!options.some((o) => o.v === value) && /^\d{4}-\d{2}$/.test(value)) {
    const [y, m] = value.split("-").map(Number);
    const d = new Date(Date.UTC(y!, m! - 1, 1));
    options.push({ v: value, label: d.toLocaleDateString("pt-BR", { month: "long", year: "numeric", timeZone: "UTC" }) });
  }
  return (
    <select className="select-sm" value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
      {options.map((o) => (
        <option key={o.v} value={o.v}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

interface UsageRowData {
  key: string;
  label: string;
  email?: string | null;
  responses: number;
  tokens: number;
  costUsd: number;
}

/** Tabela paginada do relatório de consumo (10/página, busca já aplicada no pai). */
function UsageTable({ title, rows }: { title: string; rows: UsageRowData[] }) {
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [rows.length]);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageSafe = Math.min(page, pageCount);
  const paged = rows.length > PAGE_SIZE ? rows.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE) : rows;
  const fmtTok = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(2)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));
  return (
    <div style={{ flex: "1 1 320px", minWidth: 0 }}>
      <strong style={{ fontSize: 13 }}>{title}</strong>
      {rows.length === 0 ? (
        <div className="admin-empty small" style={{ marginTop: 6 }}>Sem consumo no período.</div>
      ) : (
        <div style={{ overflowX: "auto", marginTop: 6 }}>
          <table className="admin-table" style={{ width: "100%", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Nome</th>
                <th style={{ textAlign: "right" }}>Respostas</th>
                <th style={{ textAlign: "right" }}>Tokens</th>
                <th style={{ textAlign: "right" }}>Custo (US$)</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((r) => (
                <tr key={r.key}>
                  <td>
                    {r.label}
                    {r.email && <div className="admin-user-meta">{r.email}</div>}
                  </td>
                  <td style={{ textAlign: "right" }}>{r.responses}</td>
                  <td style={{ textAlign: "right" }}>{fmtTok(r.tokens)}</td>
                  <td style={{ textAlign: "right" }}>${r.costUsd.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={pageSafe} pageCount={pageCount} onChange={setPage} />
        </div>
      )}
    </div>
  );
}

/**
 * Relatório de consumo de IA da competência: quem gasta (por usuário) e onde
 * (por workspace), com busca, paginação e export CSV. O total do mês já
 * aparece na barra de orçamento; aqui é o detalhamento.
 */
function UsageReportBlock({ qs }: { qs: string }) {
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [search, setSearch] = useState("");
  const [data, setData] = useState<{ byUser: UsageRowData[]; byWorkspace: UsageRowData[]; total: { responses: number; tokens: number; costUsd: number } } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const sep = qs ? "&" : "?";
    fetch(`/api/admin/usage-report${qs}${sep}period=${encodeURIComponent(period)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!cancelled) setData(j);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [qs, period]);

  const q = search.trim().toLowerCase();
  const filtra = (rows: UsageRowData[]) =>
    q ? rows.filter((r) => r.label.toLowerCase().includes(q) || (r.email ?? "").toLowerCase().includes(q)) : rows;

  return (
    <div className="admin-block">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
        <h4 style={{ margin: 0 }}>Consumo de IA por usuário e workspace</h4>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <MonthField value={period} onChange={setPeriod} label="Competência do relatório" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, e-mail ou workspace…"
            style={{ maxWidth: 230, padding: "5px 8px", fontSize: 12.5 }}
          />
          <button
            type="button"
            className="btn-approve"
            title="Baixar o relatório da competência em CSV"
            onClick={() => {
              const sep = qs ? "&" : "?";
              window.open(`/api/admin/usage-report${qs}${sep}period=${encodeURIComponent(period)}&format=csv`, "_blank", "noopener");
            }}
          >
            Exportar CSV
          </button>
        </div>
      </div>
      {loading ? (
        <SkeletonBlock />
      ) : !data ? (
        <div className="admin-empty small">Não consegui carregar o relatório.</div>
      ) : (
        <>
          <p className="admin-user-meta" style={{ marginBottom: 10 }}>
            Total da competência: {data.total.responses} resposta(s) · ${data.total.costUsd.toFixed(2)} em IA. Inclui o consumo da documentação automática.
          </p>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <UsageTable title="Por usuário" rows={filtra(data.byUser)} />
            <UsageTable title="Por workspace" rows={filtra(data.byWorkspace)} />
          </div>
        </>
      )}
    </div>
  );
}

function SkeletonBlock() {
  return <div className="admin-empty small">Carregando…</div>;
}

interface PlanOption {
  id: string;
  name: string;
  priceBrlMonthly: number;
  highlights: string[];
}

/**
 * Troca de plano self-service: em trial muda na hora (1ª fatura reescrita);
 * ativa, upgrade é imediato com pró-rata avulsa e downgrade fica agendado
 * pra próxima mensalidade (cancelável até lá).
 */
function PlanChangeBlock({
  billing,
  qs,
  onChanged,
}: {
  billing: BillingStatus;
  qs: string;
  onChanged: (b?: BillingStatus) => void;
}) {
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmPlan, setConfirmPlan] = useState<PlanOption | null>(null);

  useEffect(() => {
    fetch(`/api/admin/org/plan-options${qs}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { plans?: PlanOption[] } | null) => setPlans(j?.plans ?? []))
      .catch(() => {});
  }, [qs]);

  const currentId = billing.localPlanId;
  const current = plans.find((p) => p.id === currentId) ?? null;
  const trialing = billing.trial?.mode === "trialing" || billing.trial?.mode === "expired";

  const confirmText = (p: PlanOption): string => {
    if (trialing || !billing.trial?.activatedAt) {
      return `Mudar para o plano ${p.name} (${p.priceBrlMonthly.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}/mês)? A mudança vale agora e a cobrança após o teste já sai no valor novo.`;
    }
    if (current && p.priceBrlMonthly > current.priceBrlMonthly) {
      return `Fazer upgrade para ${p.name}? O plano novo vale imediatamente e a diferença do ciclo em curso é cobrada numa fatura pró-rata avulsa.`;
    }
    return `Agendar downgrade para ${p.name}? O plano atual continua até o fim do ciclo pago; o valor novo vale a partir da próxima fatura. Dá pra cancelar o agendamento até lá.`;
  };

  const change = async (planId: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await fetch(`/api/admin/org/plan${qs}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ planId }),
      });
      const j = (await r.json().catch(() => ({}))) as { error?: string; message?: string; billing?: BillingStatus };
      if (!r.ok) {
        setError(j.error ?? "falha ao mudar de plano");
        return;
      }
      setNotice(j.message ?? "Plano atualizado.");
      onChanged(j.billing);
    } finally {
      setBusy(false);
    }
  };

  const cancelPending = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`/api/admin/org/plan/pending${qs}`, { method: "DELETE" });
      const j = (await r.json().catch(() => ({}))) as { error?: string; billing?: BillingStatus };
      if (!r.ok) {
        setError(j.error ?? "falha ao cancelar o agendamento");
        return;
      }
      setNotice("Agendamento cancelado - o plano atual continua.");
      onChanged(j.billing);
    } finally {
      setBusy(false);
    }
  };

  if (plans.length === 0) return null;

  return (
    <div className="admin-block">
      <h4 style={{ marginBottom: 6 }}>Mudar de plano</h4>
      <p className="admin-user-meta" style={{ marginBottom: 10 }}>
        Upgrade vale na hora (diferença do ciclo em pró-rata avulsa); downgrade entra na próxima fatura.
        {trialing && " Em teste, a mudança vale imediatamente e a cobrança após o teste já sai no valor novo."}
      </p>
      {error && <div className="auth-error" style={{ marginBottom: 8 }}>{error}</div>}
      {notice && !error && (
        <div className="admin-empty small" style={{ color: "var(--success, #2a9d5c)", marginBottom: 8 }}>{notice}</div>
      )}
      {billing.pendingPlanId && (
        <div className="admin-empty small" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span>
            Downgrade agendado para <strong>{billing.pendingPlanName}</strong> - vale a partir da próxima fatura.
          </span>
          <button className="btn-reject" type="button" disabled={busy} onClick={() => void cancelPending()}>
            Cancelar agendamento
          </button>
        </div>
      )}
      <div className="addon-catalog-grid">
        {plans.map((p) => {
          const isCurrent = p.id === currentId;
          const isPending = p.id === billing.pendingPlanId;
          return (
            <div key={p.id} className="addon-card" title={p.highlights.join(" · ")}>
              <strong>
                {p.name}
                {isCurrent && <span className="badge ok" style={{ marginLeft: 6 }}>atual</span>}
                {isPending && <span className="badge warn" style={{ marginLeft: 6 }}>agendado</span>}
              </strong>
              <span className="addon-card-price">
                {p.priceBrlMonthly.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                <small style={{ fontWeight: 500 }}>/mês</small>
              </span>
              <span className="admin-user-meta">{p.highlights.slice(0, 2).join(" · ")}</span>
              <button
                className="btn-approve"
                type="button"
                disabled={busy || isCurrent || isPending}
                onClick={() => setConfirmPlan(p)}
              >
                {isCurrent ? "Plano atual" : isPending ? "Agendado" : "Mudar para este plano"}
              </button>
            </div>
          );
        })}
      </div>
      {confirmPlan && (
        <ConfirmModal
          title={`Mudar para ${confirmPlan.name}`}
          message={confirmText(confirmPlan)}
          confirmLabel="Confirmar mudança"
          onConfirm={() => void change(confirmPlan.id)}
          onClose={() => setConfirmPlan(null)}
        />
      )}
    </div>
  );
}

const ADDON_KIND_PT: Record<AddonKind, string> = {
  repos: "Repositórios extras",
  ai_budget: "Orçamento de IA extra",
  api_calls: "API extra",
  seats: "Usuários extras",
  workspaces: "Workspaces extras",
};

/** Como o extra do pacote é exibido ("+N …"). */
function fmtAddonQty(kind: AddonKind, qty: number): string {
  switch (kind) {
    case "repos":
      return `+${qty} repositório(s)`;
    case "ai_budget":
      return `+US$ ${qty.toFixed(2)}/mês`;
    case "api_calls":
      return `+${qty.toLocaleString("pt-BR")} req/mês`;
    case "seats":
      return `+${qty} usuário(s)`;
    case "workspaces":
      return `+${qty} workspace(s)`;
  }
}

/** Rótulo do campo de quantidade no formulário, por tipo. */
const ADDON_QTY_LABEL: Record<AddonKind, string> = {
  repos: "Repositórios",
  ai_budget: "US$ por mês",
  api_calls: "Req/mês",
  seats: "Usuários",
  workspaces: "Workspaces",
};

/**
 * Pacotes adicionais da org: repositórios extras e orçamento de IA extra,
 * aplicados pelo sysadmin por cima dos limites do plano (valem também durante
 * o trial). A aplicação pode gerar cobrança (fatura local, pagável online) ou
 * ser cortesia. Remover é soft: o histórico fica registrado.
 */
function AddonsBlock({
  billing,
  qs,
  isSysadmin,
  onChanged,
}: {
  billing: BillingStatus;
  qs: string;
  isSysadmin: boolean;
  onChanged: (b?: BillingStatus) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState<AddonRow["kind"]>("repos");
  const [qty, setQty] = useState(5);
  const [label, setLabel] = useState("");
  const [note, setNote] = useState("");
  const [charge, setCharge] = useState(true);
  const [amountBrl, setAmountBrl] = useState(49.9);
  const [catalog, setCatalog] = useState<CatalogPackageRow[]>([]);
  const trialing = billing.trial?.mode === "trialing";
  const defaultDue = () =>
    trialing && billing.trial?.endsAt
      ? billing.trial.endsAt.slice(0, 10)
      : new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10);
  const [dueDate, setDueDate] = useState(defaultDue);
  // remoção em duas etapas (sem window.confirm): 1º clique arma, 2º confirma
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  const items = billing.addons?.items ?? [];
  const active = items.filter((a) => !a.removedAt);
  const removed = items.filter((a) => a.removedAt);

  useEffect(() => {
    fetch("/api/admin/addon-packages")
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { packages?: CatalogPackageRow[] } | null) => setCatalog((j?.packages ?? []).filter((p) => p.active)))
      .catch(() => {});
  }, []);

  /** Abre o Checkout Pro da fatura do pacote (nova aba; popup bloqueado cai na mesma). */
  const payInvoice = async (invoiceId: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`/api/admin/invoices/${invoiceId}/checkout${qs}`, { method: "POST" });
      const j = (await r.json().catch(() => ({}))) as { url?: string; error?: string };
      if (!r.ok || !j.url) {
        setError(j.error ?? "não foi possível iniciar o pagamento");
        return;
      }
      const win = window.open(j.url, "_blank", "noopener");
      if (!win) window.location.href = j.url;
    } finally {
      setBusy(false);
    }
  };

  /** Contrata/aplica. Admin comum manda só packageId (cobrança obrigatória). */
  const apply = async (body: Record<string, unknown>, payAfter: boolean) => {
    setBusy(true);
    setError(null);
    let invoiceToPay: string | null = null;
    try {
      const r = await fetch(`/api/admin/org/addons${qs}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError((j as { error?: string }).error ?? "falha ao aplicar o pacote");
        return;
      }
      setLabel("");
      setNote("");
      const inv = (j as { invoice?: { id?: string; status?: string } | null }).invoice;
      if (payAfter && inv?.id && inv.status === "open") invoiceToPay = inv.id;
      onChanged((j as { billing?: BillingStatus }).billing);
    } finally {
      setBusy(false);
    }
    // contratou com cobrança: já abre o pagamento (o pacote libera na aprovação)
    if (invoiceToPay) await payInvoice(invoiceToPay);
  };

  const applyCustom = () =>
    apply(
      {
        kind,
        qty,
        label: label.trim() || undefined,
        note: note.trim() || undefined,
        charge,
        ...(charge ? { amountBrl, dueDate } : {}),
      },
      false,
    );

  const remove = async (addonId: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`/api/admin/org/addons/${addonId}${qs}`, { method: "DELETE" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError((j as { error?: string }).error ?? "falha ao remover o pacote");
        return;
      }
      setConfirmRemoveId(null);
      onChanged((j as { billing?: BillingStatus }).billing);
    } finally {
      setBusy(false);
    }
  };

  const fmtQty = (a: AddonRow) => fmtAddonQty(a.kind, a.qty);
  const chargeBadge = (a: AddonRow) => {
    if (!a.invoiceId) return <span className="badge ok">cortesia</span>;
    const valor =
      typeof a.invoiceAmountBrl === "number"
        ? ` ${a.invoiceAmountBrl.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}`
        : "";
    if (a.invoiceStatus === "paid") return <span className="badge ok">fatura paga{valor}</span>;
    if (a.invoiceStatus === "canceled") return <span className="badge">fatura cancelada</span>;
    return <span className="badge warn">aguardando pagamento{valor}</span>;
  };
  const isPending = (a: AddonRow) => !a.removedAt && (a.status === "pending" || (a.status === undefined && a.invoiceStatus === "open"));

  return (
    <div className="admin-block">
      <h4 style={{ marginBottom: 6 }}>Pacotes adicionais</h4>
      <p className="admin-user-meta" style={{ marginBottom: 10 }}>
        Extras por cima dos limites do plano. Pacote é ASSINATURA MENSAL: a 1ª fatura libera o pacote na
        aprovação do pagamento e, a partir daí, o valor entra na sua fatura mensal junto do plano (mesmo ciclo).
        Vale inclusive durante o período de teste.
        {trialing &&
          " Esta organização está em teste: os limites vigentes são os do menor plano e os pacotes liberados somam sobre eles."}
      </p>
      {error && <div className="auth-error" style={{ marginBottom: 8 }}>{error}</div>}

      {catalog.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <strong style={{ fontSize: 13 }}>Catálogo disponível</strong>
          <div className="addon-catalog">
            {(Object.keys(ADDON_KIND_PT) as AddonKind[])
              .map((k) => ({ kind: k, packages: catalog.filter((p) => p.kind === k) }))
              .filter((g) => g.packages.length > 0)
              .map((g) => (
                <div key={g.kind}>
                  <div className="addon-catalog-group-label">{ADDON_KIND_PT[g.kind]}</div>
                  <div className="addon-catalog-grid">
                    {g.packages.map((p) => (
                      <div key={p.id} className="addon-card" title={p.label}>
                        <strong>{fmtAddonQty(p.kind, p.qty)}</strong>
                        <span className="addon-card-price">
                          {p.priceBrl.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                          <small style={{ fontWeight: 500 }}>/mês</small>
                        </span>
                        <button
                          className="btn-approve"
                          type="button"
                          disabled={busy}
                          onClick={() => void apply({ packageId: p.id }, true)}
                        >
                          Contratar e pagar
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
          <p className="admin-user-meta" style={{ marginTop: 8 }}>
            A contratação gera a 1ª fatura na hora e abre o pagamento online; o pacote é liberado com a
            aprovação e passa a compor a sua mensalidade (assinatura recorrente - remova o pacote pra parar).
          </p>
        </div>
      )}
      {catalog.length === 0 && !isSysadmin && (
        <div className="admin-empty small" style={{ marginBottom: 12 }}>
          Nenhum pacote disponível pra contratação no momento.
        </div>
      )}

      {isSysadmin && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 12 }}>
          <label className="field" style={{ width: 190 }}>
            <span>Tipo (pacote avulso)</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as AddonKind)}>
              {(Object.keys(ADDON_KIND_PT) as AddonKind[]).map((k) => (
                <option key={k} value={k}>
                  {ADDON_KIND_PT[k]}
                </option>
              ))}
            </select>
          </label>
          <label className="field" style={{ width: 130 }}>
            <span>{ADDON_QTY_LABEL[kind]}</span>
            <input
              type="number"
              min={kind === "ai_budget" ? 0.01 : 1}
              step={kind === "ai_budget" ? 0.01 : 1}
              value={qty}
              onChange={(e) => setQty(Number(e.target.value))}
            />
          </label>
          <label className="field" style={{ flex: "1 1 200px" }}>
            <span>Descrição (opcional)</span>
            <input type="text" value={label} placeholder="ex.: negociação Q4" onChange={(e) => setLabel(e.target.value)} />
          </label>
          <label className="field" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 6, width: "auto" }}>
            <input type="checkbox" checked={charge} onChange={(e) => setCharge(e.target.checked)} />
            <span>Gerar cobrança</span>
          </label>
          {charge && (
            <>
              <label className="field" style={{ width: 130 }}>
                <span>Valor (R$)</span>
                <input type="number" min={0.01} step="0.01" value={amountBrl} onChange={(e) => setAmountBrl(Number(e.target.value))} />
              </label>
              <label className="field" style={{ width: 160 }}>
                <span>Vencimento</span>
                <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </label>
            </>
          )}
          <label className="field" style={{ flex: "1 1 160px" }}>
            <span>Observação (opcional)</span>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <button className="btn-approve" type="button" disabled={busy} onClick={() => void applyCustom()}>
            Aplicar pacote
          </button>
        </div>
      )}
      {isSysadmin && charge && (
        <p className="admin-user-meta" style={{ marginTop: -6, marginBottom: 10 }}>
          Com cobrança, o pacote fica pendente e só é liberado quando a fatura for paga (ou marcada como paga).
          {trialing && " Vencimento sugerido acompanha o fim do período de teste (mesma regra da 1ª mensalidade)."}
        </p>
      )}

      {active.length === 0 && removed.length === 0 ? (
        <div className="admin-empty small">Nenhum pacote aplicado.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="admin-table" style={{ width: "100%", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Pacote</th>
                <th style={{ textAlign: "left" }}>Extra</th>
                <th style={{ textAlign: "left" }}>Cobrança</th>
                <th style={{ textAlign: "left" }}>Aplicado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {[...active, ...removed].map((a) => (
                <tr key={a.id} style={a.removedAt ? { opacity: 0.55 } : undefined}>
                  <td>
                    <span style={{ fontWeight: 600 }}>{a.label}</span>
                    <div className="admin-user-meta">{ADDON_KIND_PT[a.kind]}</div>
                    {a.note && <div className="admin-user-meta">{a.note}</div>}
                  </td>
                  <td>{fmtQty(a)}</td>
                  <td>
                    {chargeBadge(a)}
                    {a.removedAt && (
                      <span className="badge err" style={{ marginLeft: 6 }}>
                        removido em {new Date(a.removedAt).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })}
                      </span>
                    )}
                  </td>
                  <td>
                    {new Date(a.appliedAt).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })}
                    <div className="admin-user-meta">{a.appliedBy}</div>
                  </td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {isPending(a) && a.invoiceId && a.invoiceStatus === "open" && (
                      <>
                        <button
                          className="btn-approve"
                          type="button"
                          disabled={busy}
                          title="Pagar online (Mercado Pago)"
                          onClick={() => void payInvoice(a.invoiceId!)}
                        >
                          Pagar
                        </button>{" "}
                      </>
                    )}
                    {!a.removedAt &&
                      (isSysadmin || isPending(a)) &&
                      (confirmRemoveId === a.id ? (
                        <>
                          <button className="btn-reject" type="button" disabled={busy} onClick={() => void remove(a.id)}>
                            Confirmar
                          </button>{" "}
                          <button className="btn-approve" type="button" disabled={busy} onClick={() => setConfirmRemoveId(null)}>
                            Voltar
                          </button>
                        </>
                      ) : (
                        <button className="btn-reject" type="button" disabled={busy} onClick={() => setConfirmRemoveId(a.id)}>
                          {isPending(a) && !isSysadmin ? "Cancelar contratação" : "Remover"}
                        </button>
                      ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="admin-user-meta" style={{ marginTop: 8 }}>
        A 1ª fatura do pacote não mexe no ciclo da mensalidade: pagar libera o pacote sem estender a cobertura
        do plano - depois o valor recorre dentro da fatura mensal. Cancelar/remover derruba a fatura em aberto
        vinculada e tira o pacote das próximas mensalidades.
      </p>
    </div>
  );
}

interface InvoiceRow {
  id: string;
  planName: string;
  amountBrl: number;
  period: string;
  dueDate: string;
  status: "open" | "paid" | "canceled";
  paidAt?: string | null;
  note?: string | null;
}

const INVOICE_STATUS_PT: Record<InvoiceRow["status"], string> = {
  open: "em aberto",
  paid: "paga",
  canceled: "cancelada",
};

function fmtPeriod(p: string): string {
  const [y, m] = p.split("-");
  return `${m}/${y}`;
}

/**
 * Gera o link do Checkout Pro da fatura e abre em nova aba (popup bloqueado
 * cai na mesma aba). Devolve a mensagem de erro, ou null em caso de sucesso.
 */
async function startInvoiceCheckout(invoiceId: string, qs: string): Promise<string | null> {
  const r = await fetch(`/api/admin/invoices/${invoiceId}/checkout${qs}`, { method: "POST" });
  const j = (await r.json().catch(() => ({}))) as { url?: string; error?: string };
  if (!r.ok || !j.url) return j.error ?? "não foi possível iniciar o pagamento";
  const win = window.open(j.url, "_blank", "noopener");
  if (!win) window.location.href = j.url;
  return null;
}

/**
 * Fatura em aberto em DESTAQUE, ao lado do plano - pagamento a um clique.
 * Mostra a mais urgente (menor vencimento); havendo outras, aponta pra lista.
 */
function OpenInvoiceCard({ invoice, count, qs }: { invoice: InvoiceRow; count: number; qs: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const overdue = new Date(invoice.dueDate).getTime() < Date.now();
  const pay = async () => {
    setBusy(true);
    setError(null);
    try {
      setError(await startInvoiceCheckout(invoice.id, qs));
    } finally {
      setBusy(false);
    }
  };
  return (
    <aside className={`open-invoice-card${overdue ? " overdue" : ""}`} aria-label="Fatura em aberto">
      <span className="open-invoice-title">{overdue ? "Fatura vencida" : "Fatura em aberto"}</span>
      <strong className="open-invoice-amount">
        {invoice.amountBrl.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
      </strong>
      <span className="admin-user-meta">
        {invoice.planName} · competência {fmtPeriod(invoice.period)}
      </span>
      <span className="admin-user-meta">
        {overdue ? "venceu em" : "vence em"}{" "}
        {new Date(invoice.dueDate).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })}
      </span>
      {error && <span className="open-invoice-error">{error}</span>}
      <button className="btn-approve" type="button" disabled={busy} title="Pagar online (Mercado Pago)" onClick={() => void pay()}>
        Pagar agora
      </button>
      {count > 1 && <span className="admin-user-meta">+{count - 1} outra(s) em aberto na lista de faturas abaixo.</span>}
    </aside>
  );
}



/** Faturas da org - lista pra todo admin; gerar/pagar/cancelar só sysadmin. */
function InvoicesBlock({
  items,
  qs,
  isSysadmin,
  onChanged,
}: {
  items: InvoiceRow[];
  qs: string;
  isSysadmin: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  // edição inline (sysadmin): fatura em aberto - valor, vencimento, observação
  const [editing, setEditing] = useState<{ id: string; amountBrl: number; dueDate: string; note: string } | null>(null);
  // filtro + busca + paginação (padrão das listas do projeto)
  const [statusFilter, setStatusFilter] = useState<"" | InvoiceRow["status"]>("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  useEffect(() => {
    // mudou busca/filtro: volta pra 1ª página (a atual pode nem existir mais)
    setPage(1);
  }, [search, statusFilter]);

  const call = async (url: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(url, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body ?? {}),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(j.error ?? "falha na operação");
        return;
      }
      onChanged(); // pagar fatura ativa a assinatura - recarrega status e lista no pai
    } finally {
      setBusy(false);
    }
  };

  /** Salva a edição da fatura (sysadmin). */
  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`/api/admin/invoices/${editing.id}${qs}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ amountBrl: editing.amountBrl, dueDate: editing.dueDate, note: editing.note }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(j.error ?? "falha ao editar a fatura");
        return;
      }
      setEditing(null);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  /** Pagamento online: gera o link do Checkout Pro e redireciona. */
  const payOnline = async (invoiceId: string) => {
    setBusy(true);
    setError(null);
    try {
      setError(await startInvoiceCheckout(invoiceId, qs));
    } finally {
      setBusy(false);
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = items
    .filter((i) => !statusFilter || i.status === statusFilter)
    .filter(
      (i) =>
        !q ||
        i.period.includes(q) ||
        fmtPeriod(i.period).includes(q) ||
        i.planName.toLowerCase().includes(q) ||
        (i.note ?? "").toLowerCase().includes(q),
    )
    .sort((a, b) => b.dueDate.localeCompare(a.dueDate));
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, pageCount);
  const paged = filtered.length > PAGE_SIZE ? filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE) : filtered;

  return (
    <div className="admin-block">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
        <h4 style={{ margin: 0 }}>Faturas{items.length > 0 ? ` (${filtered.length})` : ""}</h4>
        {items.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <select
              className="select-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as "" | InvoiceRow["status"])}
              aria-label="Filtrar faturas por status"
            >
              <option value="">Todas</option>
              <option value="open">Em aberto</option>
              <option value="paid">Pagas</option>
              <option value="canceled">Canceladas</option>
            </select>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por competência, plano ou observação…"
              style={{ maxWidth: 250, padding: "5px 8px", fontSize: 12.5 }}
            />
            <button
              type="button"
              className="btn-approve"
              title="Baixar o histórico completo de faturas em CSV"
              onClick={() => window.open(`/api/admin/invoices/export.csv${qs}`, "_blank", "noopener")}
            >
              Exportar CSV
            </button>
          </div>
        )}
      </div>
      <p className="admin-user-meta" style={{ marginBottom: 10 }}>
        Mensalidades da organização (valor congelado na emissão; vencimento dia 10 do mês seguinte à
        competência). Pagamento registrado aqui ativa a assinatura.
      </p>
      {error && <div className="auth-error" style={{ marginBottom: 8 }}>{error}</div>}

      {isSysadmin && (
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 12 }}>
          <label className="field" style={{ width: 190 }}>
            <span>Competência</span>
            <MonthField value={period} onChange={setPeriod} label="Competência da fatura" />
          </label>
          <button
            className="btn-approve"
            type="button"
            disabled={busy}
            onClick={() => void call(`/api/admin/invoices${qs}`, { period })}
          >
            Gerar fatura
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <div className="admin-empty small">Nenhuma fatura ainda.</div>
      ) : filtered.length === 0 ? (
        <div className="admin-empty small">Nenhuma fatura encontrada com esse filtro.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="admin-table" style={{ width: "100%", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Competência</th>
                <th style={{ textAlign: "left" }}>Plano</th>
                <th style={{ textAlign: "right" }}>Valor</th>
                <th style={{ textAlign: "left" }}>Vencimento</th>
                <th style={{ textAlign: "left" }}>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {paged.map((i) => {
                const overdue = i.status === "open" && new Date(i.dueDate).getTime() < Date.now();
                if (editing && editing.id === i.id) {
                  return (
                    <tr key={i.id}>
                      <td>{fmtPeriod(i.period)}</td>
                      <td>{i.planName}</td>
                      <td style={{ textAlign: "right" }}>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={editing.amountBrl}
                          onChange={(e) => setEditing({ ...editing, amountBrl: Number(e.target.value) })}
                          style={{ width: 100, padding: "4px 6px", textAlign: "right" }}
                        />
                      </td>
                      <td>
                        <input
                          type="date"
                          value={editing.dueDate.slice(0, 10)}
                          onChange={(e) => setEditing({ ...editing, dueDate: e.target.value })}
                          style={{ padding: "4px 6px" }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={editing.note}
                          placeholder="observação"
                          onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                          style={{ width: 140, padding: "4px 6px" }}
                        />
                      </td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <button className="btn-approve" type="button" disabled={busy} onClick={() => void saveEdit()}>
                          Salvar
                        </button>{" "}
                        <button className="btn-reject" type="button" disabled={busy} onClick={() => setEditing(null)}>
                          Cancelar
                        </button>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={i.id}>
                    <td>{fmtPeriod(i.period)}</td>
                    <td>{i.planName}</td>
                    <td style={{ textAlign: "right" }}>
                      {i.amountBrl.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                    </td>
                    <td>{new Date(i.dueDate).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })}</td>
                    <td>
                      <span className={`badge ${i.status === "paid" ? "ok" : overdue ? "err" : i.status === "canceled" ? "" : "warn"}`}>
                        {overdue ? "vencida" : INVOICE_STATUS_PT[i.status]}
                      </span>
                      {i.note && <span className="admin-user-meta" style={{ marginLeft: 6 }}>{i.note}</span>}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {i.status === "paid" && (
                        <button
                          className="btn-approve"
                          type="button"
                          title="Abrir o recibo de pagamento (PDF)"
                          onClick={() => window.open(`/api/admin/invoices/${encodeURIComponent(i.id)}/receipt.pdf${qs}`, "_blank", "noopener")}
                        >
                          Recibo
                        </button>
                      )}
                      {i.status === "open" && (
                        <>
                          <button
                            className="btn-approve"
                            type="button"
                            disabled={busy}
                            title="Pagar online (Mercado Pago)"
                            onClick={() => void payOnline(i.id)}
                          >
                            Pagar
                          </button>
                          {isSysadmin && (
                            <>
                              {" "}
                              <button
                                className="btn-approve"
                                type="button"
                                disabled={busy}
                                onClick={() => setEditing({ id: i.id, amountBrl: i.amountBrl, dueDate: i.dueDate, note: i.note ?? "" })}
                              >
                                Editar
                              </button>{" "}
                              <button className="btn-approve" type="button" disabled={busy} onClick={() => void call(`/api/admin/invoices/${i.id}/pay${qs}`)}>
                                Marcar paga
                              </button>{" "}
                              <button className="btn-reject" type="button" disabled={busy} onClick={() => void call(`/api/admin/invoices/${i.id}/cancel${qs}`)}>
                                Cancelar
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination page={pageSafe} pageCount={pageCount} onChange={setPage} label={`${filtered.length} fatura(s)`} />
        </div>
      )}
    </div>
  );
}
